import { combineLatest, fromEvent, merge, of } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, tap } from 'rxjs/operators';

import { userInfo$, postText, deleteEntity } from './http.js';
import { findKnowledge, exportKnowledge } from './knows.js';
import { showError } from './ui.js';
import { buildWorkMonitor } from './working.js';

document.forms.addKnowledge.knowledge.focus();

let populateStoreOptions$ = userInfo$.pipe(
    map(userInfo => {
        let stores = [];
        for (let key of Object.keys(userInfo.knowledge)) {
            stores.push(userInfo.knowledge[key]);
        }
        return stores;
    }),
    tap(stores => {
        let select = document.forms.addKnowledge.store;
        select.innerHTML = '';
        for (let i = 0; i < stores.length; ++i) {
            let s = stores[i];
            let opt = document.createElement('option');
            opt.value = s;
            opt.textContent = s;
            select.appendChild(opt);
            if (i === 0) {
                select.value = opt.value;
            }
        }
        select.disabled = false;
    }),
);

let saveMonitor = buildWorkMonitor();

let saveIndicator$ = saveMonitor.showElements('.saving');
let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();

let submitAddKnowledge$ = fromEvent(document.forms.addKnowledge, 'submit').pipe(
    map(event => {
        event.preventDefault();
        let knowledge = document.forms.addKnowledge.knowledge.value;
        document.forms.addKnowledge.knowledge.value = '';
        document.forms.addKnowledge.knowledge.focus();
        return knowledge;
    }),
    mergeMap(knowledge => of(knowledge).pipe(
        map(knowledge => knowledge.trim()),
        filter(knowledge => !!knowledge),
        switchMap(knowledge => saveMonitor.zone(postText('/api/add-knowledge', knowledge))),
    ), 3),
);

let exportKnowledge$ = fromEvent(document.getElementById('export-knowledge-btn'), 'click').pipe(
    switchMap(() => {
        let storeName = document.forms.addKnowledge.store.value;
        return exportKnowledge(storeName).pipe(
            tap(knowledgeExport => {
                let jsonString = JSON.stringify(knowledgeExport);
                let blob = new Blob([jsonString], { type: 'application/json' });
                let url = URL.createObjectURL(blob);
                let link = document.createElement('a');
                link.href = url;
                link.download = `knowledge_export_${storeName}.json`;
                link.click();
                URL.revokeObjectURL(url);
            }),
        );
    }),
);

let findKnowledgeTrigger$ = merge(
    fromEvent(document.forms.addKnowledge.knowledge, 'input').pipe(debounceTime(300)),
    submitAddKnowledge$,
);

let knowledgeStore$ = merge(
    fromEvent(document.forms.addKnowledge.store, 'change'),
    populateStoreOptions$,
).pipe(
    startWith(undefined),
    map(() => document.forms.addKnowledge.store.value),
);

let addedKnowledge$ = findKnowledgeTrigger$.pipe(
    map(() => (document.forms.addKnowledge.knowledge.value || '').trim()),
    distinctUntilChanged(),
);

let findKnowledge$ = combineLatest([
    knowledgeStore$,
    addedKnowledge$,
]).pipe(
    switchMap(([storeName, context]) => {
        let container = document.getElementById('found-knowledge');
        container.innerHTML = 'Loading…';
        return findKnowledge(storeName, context).pipe(
            map(knows => knows.map(k => ({
                ...k,
                scoreColor: calcScoreColor(k),
            }))),
            tap(knows => {
                container.innerHTML = '';
                for (let obj of knows) {
                    let el = renderKnowledgeEdit(obj);
                    container.appendChild(el);
                }
            }),
        );
    }),
);

function calcScoreColor(know) {
    // 0=bad, 1=good
    let score = 1 - (know.distance - 0.2) * 3.5;
    let r = Math.max(0, Math.min(255, 255 * (1 - 2*score)));
    let g = Math.max(0, Math.min(255, 255 * 2*(score-0.5)));
    return `rgb(${r}, ${g}, 64)`;
}

function renderKnowledgeEdit(obj) {
    let form = document.createElement('form');
    form.setAttribute('data-id', obj.id);
    form.addEventListener('submit', handleUpdateKnowledgeSubmit, false);
    let score = document.createElement('div');
    score.classList.add('score');
    score.style.backgroundColor = obj.scoreColor;
    score.title = `dist=${obj.distance}`;
    form.appendChild(score);
    let input = document.createElement('textarea');
    input.name = 'knowledge';
    input.textContent = obj.knowledge;
    let fc = createFormControl('knowledge', input);
    fc.classList.add('knowledge');
    form.appendChild(fc);
    let btns = document.createElement('div');
    btns.classList.add('actions');
    form.appendChild(btns);
    let submit = document.createElement('button');
    submit.textContent = 'save';
    btns.appendChild(submit);
    let rm = document.createElement('button');
    rm.type = 'button';
    rm.textContent = 'remove';
    rm.addEventListener('click', handleDeleteKnowledge, false);
    btns.appendChild(rm);
    return form;
}

function handleUpdateKnowledgeSubmit(event) {
    event.preventDefault();
    let form = event.target;
    let id = form.getAttribute('data-id');
    let knowledge = form.knowledge.value;
    saveMonitor.zone(postText(`/api/knowledge/${id}`, knowledge)).subscribe({
        error: err => showError(err),
    });
}

function handleDeleteKnowledge(event) {
    let form = findParent(event.target, p => p.tagName === 'FORM');
    let id = form.getAttribute('data-id');
    for (let el of form.querySelectorAll('button, textarea')) {
        el.disabled = true;
    }
    saveMonitor.zone(deleteEntity(`/api/knowledge/${id}`)).subscribe({
        error: err => showError(err),
    });
}

function findParent(el, condition) {
    while (el) {
        if (condition(el)) {
            break;
        }
        el = el.parentElement;
    }
    return el;
}

function createFormControl(label, input) {
    let fc = document.createElement('div');
    fc.classList.add('form-control');
    let labelEl = document.createElement('label');
    labelEl.textContent = label;
    fc.appendChild(labelEl);
    fc.appendChild(input);
    return fc;
}

merge(
    saveIndicator$,
    alertSavingBeforeUnload$,
    findKnowledge$,
    exportKnowledge$,
).pipe().subscribe({
    error: err => showError(err),
});
